import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import TextMAS from "components/TextMAS/TextMAS";
import s from "./StatisticsOnlineTransaction.module.scss";
import { fetchStatisticsCardType, fetchStatisticsChange } from "./model";
import AnalyticsTable from "components/AnalyticsTable/AnalyticsTable";
import { amountConversion, isNullish } from "utils/utils";
import useSnackbarMAS from "utils/useSnackbarMAS/useSnackbarMAS";
import { useAuth } from "utils/useAuth/useAuth";
import { Stack } from "@mui/material";
import Card from "components/Card/Card";
import PageContainer from "components/PageContainer/PageContainer";

const PAGE_LOCALE_KEY = "page.analytics.systemStatistics.onlineTransaction.";
const NO_DATA_PLACEHOLDER = "-";

const StatisticsOnlineTransaction = ({ focus }) => {
  const { t } = useTranslation();
  const { snackbarError } = useSnackbarMAS();
  const { logout } = useAuth();

  const [isChangeLoading, setIsChangeLoading] = useState(true);
  const [isChangeError, setIsChangeError] = useState(false);
  const [isCardTypeLoading, setIsCardTypeLoading] = useState(true);
  const [isCardTypeError, setIsCardTypeError] = useState(false);
  const isLoading = isChangeLoading || isCardTypeLoading;
  const [hasFetched, setHasFetched] = useState(false);

  const [isUnauthorized, setIsUnauthorized] = useState(false);
  const [dataSource, setDataSource] = useState({
    yearlyStats: [],
    monthlyStats: [],
    byCardType: [],
  });

  useEffect(() => {
    if (focus && !hasFetched) {
      getStatisticsChange();
      getStatisticsCardType();
      setHasFetched(true);
    }
  }, [focus]);

  const apiStatusHandler = (status, setIsLoading, setError, errorToast) => {
    switch (status) {
      case 401: {
        console.log("You have no authorization to view the page");
        return logout("TOKEN_EXPIRED");
      }
      case 403: {
        setIsUnauthorized(true);
        setIsLoading(false);
        return;
      }
      default: {
        errorToast();
        setError(true);
        setIsLoading(false);
        return;
      }
      case 200:
        break;
    }
  };

  const getStatisticsChange = async () => {
    const showErrorToast = () =>
      snackbarError({
        message: t(PAGE_LOCALE_KEY + "fetchingError.transactionStat"),
      });
    setIsChangeLoading(true);
    const response = await fetchStatisticsChange();

    if (!response) {
      setIsChangeError("networkError");
      setIsChangeLoading(false);
      return;
    }

    apiStatusHandler(
      response?.status,
      setIsChangeLoading,
      setIsChangeError,
      showErrorToast,
    );

    const result = response?.data;
    switch (result?.responseCode) {
      case "0000":
        setDataSource((prev) => ({ ...prev, ...result.data }));
        break;
      case "1200":
        setIsUnauthorized(true);
        break;
      default:
        showErrorToast();
        break;
    }
    setIsChangeLoading(false);
  };

  const getStatisticsCardType = async () => {
    const showErrorToast = () =>
      snackbarError({
        message: t(PAGE_LOCALE_KEY + "fetchingError.transactionCardType"),
      });
    setIsCardTypeLoading(true);
    const response = await fetchStatisticsCardType();

    if (!response) {
      setIsCardTypeError("networkError");
      setIsCardTypeLoading(false);
      return;
    }

    apiStatusHandler(
      response?.status,
      setIsCardTypeLoading,
      setIsCardTypeError,
      showErrorToast,
    );

    const result = response?.data;
    switch (result?.responseCode) {
      case "0000":
        const parsedResult = [
          {
            description: "total",
            volume: result?.data.totalVolume,
            count: result?.data.totalCount,
            volumeBbmsl: result?.data.totalVolumeBbmsl,
            countBbmsl: result?.data.totalCountBbmsl,
          },
          ...result?.data.breakdown,
        ];
        setDataSource((prev) => ({ ...prev, byCardType: parsedResult }));
        break;
      case "1200":
        setIsUnauthorized(true);
        break;
      default:
        showErrorToast();
    }
    setIsCardTypeLoading(false);
  };

  const renderTransactionChangeCell = (
    count,
    countChange,
    changePercentage,
    currency,
  ) => {
    const statChangeColorClass = !countChange
      ? s["change-stat-grey"]
      : countChange > 0
        ? s["change-stat-green"]
        : s["change-stat-red"];

    const amountSymbol = !countChange ? "" : countChange > 0 ? "+" : "-";

    const amountText = (
      <TextMAS fontSize="18px">
        {(currency?.concat(" ") ?? "") + amountConversion(count)}
      </TextMAS>
    );

    return (
      <div
        className={
          currency ? s["volume-cell-container"] : s["count-cell-container"]
        }
      >
        {amountText}
        <div
          className={`${s["change-stat-container"]} ${statChangeColorClass}`}
        >
          <TextMAS fontSize="small" className={statChangeColorClass}>
            {amountSymbol + amountConversion(Math.abs(countChange))}
          </TextMAS>
          &nbsp;
          <TextMAS fontSize="small" className={statChangeColorClass}>
            {"(" + (changePercentage ?? "--") + "%)"}
          </TextMAS>
        </div>
      </div>
    );
  };

  const renderTransactionCardTypeCell = (volume, change) => {
    return (
      <div className={s["card-type-cell-container"]}>
        <TextMAS fontSize="18px">
          {!isNullish(volume) ? "HKD " + amountConversion(volume) : "-"}
        </TextMAS>
        <TextMAS fontSize="small" className={s["change-stat-grey"]}>
          {"(" + (!isNullish(change) ? amountConversion(change) : "-") + ")"}
        </TextMAS>
      </div>
    );
  };

  const transactionYearChangeColumns = [
    {
      id: "year",
      title: t(PAGE_LOCALE_KEY + "transactionYearChange.columnTitle.1"),
      key: "date",
      headerStyle: { width: "160px" },
      render: ({ data, key }) => {
        return <TextMAS fontSize="large">{data[key]}</TextMAS>;
      },
    },
    {
      id: "volume",
      title: t(PAGE_LOCALE_KEY + "transactionYearChange.columnTitle.3"),
      key: "totalVolume",
      render: ({ data, key }) => {
        return renderTransactionChangeCell(
          data[key],
          data[key + "Change"],
          data[key + "ChangePercentage"],
          "HKD",
        );
      },
    },
    {
      id: "count",
      title: t(PAGE_LOCALE_KEY + "transactionYearChange.columnTitle.2"),
      key: "totalCount",
      render: ({ data, key }) => {
        return renderTransactionChangeCell(
          data[key],
          data[key + "Change"],
          data[key + "ChangePercentage"],
        );
      },
    },
  ];

  const transactionMonthChangeColumns = [
    {
      id: "year",
      title: t(PAGE_LOCALE_KEY + "transactionMonthChange.columnTitle.1"),
      key: "date",
      headerStyle: { width: "160px" },
      render: ({ data, key }) => {
        return <TextMAS fontSize="large">{data[key]}</TextMAS>;
      },
    },
    {
      id: "volume",
      title: t(PAGE_LOCALE_KEY + "transactionMonthChange.columnTitle.3"),
      key: "totalVolume",
      render: ({ data, key }) => {
        return renderTransactionChangeCell(
          data[key],
          data[key + "Change"],
          data[key + "ChangePercentage"],
          "HKD",
        );
      },
    },
    {
      id: "count",
      title: t(PAGE_LOCALE_KEY + "transactionMonthChange.columnTitle.2"),
      key: "totalCount",
      render: ({ data, key }) => {
        return renderTransactionChangeCell(
          data[key],
          data[key + "Change"],
          data[key + "ChangePercentage"],
        );
      },
    },
  ];

  const transactionCardTypeColumns = [
    {
      id: "cardType",
      title: t(PAGE_LOCALE_KEY + "transactionCardType.columnTitle.1"),
      key: "description",
      headerStyle: { width: "160px" },
      render: ({ data, key }) => {
        return (
          <TextMAS fontSize="large">
            {data[key].charAt(0).toUpperCase() + data[key].slice(1)}
          </TextMAS>
        );
      },
    },
    {
      id: "volume",
      title: (
        <>
          <TextMAS fontWeight="semibold">
            {t(PAGE_LOCALE_KEY + "transactionCardType.columnTitle.2.1")}
          </TextMAS>
          &nbsp;
          <TextMAS fontSize="small" color="#999">
            {t(PAGE_LOCALE_KEY + "transactionCardType.columnTitle.2.2")}
          </TextMAS>
        </>
      ),
      key: "volume",
      sorting: true,
      render: ({ data }) => {
        return renderTransactionCardTypeCell(data?.volume, data?.count);
      },
    },
    {
      id: "settle",
      title: (
        <>
          <TextMAS fontWeight="semibold">
            {t(PAGE_LOCALE_KEY + "transactionCardType.columnTitle.3.1")}
          </TextMAS>
          &nbsp;
          <TextMAS fontSize="small" color="#999">
            {t(PAGE_LOCALE_KEY + "transactionCardType.columnTitle.3.2")}
          </TextMAS>
        </>
      ),
      key: "settle",
      render: ({ data }) => {
        return renderTransactionCardTypeCell(
          data?.volumeBbmsl,
          data?.countBbmsl,
        );
      },
    },
  ];

  return (
    <PageContainer
      isLoading={isLoading}
      unauthorized={isUnauthorized}
      errorMessage={
        isChangeError === isCardTypeError
          ? isChangeError
          : isChangeError && isCardTypeError
      }
    >
      <div className={s["analytics-transaction-online-container"]}>
        <div className={s["last-modified-container"]}>
          <TextMAS>
            {t(PAGE_LOCALE_KEY + "lastUpdated") + ": "}
            {dataSource?.latestUpdate
              ? moment(dataSource?.latestUpdate).format("YYYY-MM-DD HH:mm")
              : NO_DATA_PLACEHOLDER}
          </TextMAS>
        </div>

        <Stack className={s["transaction-info-card-container"]}>
          <Card>
            <Stack direction="column">
              <TextMAS className={s["transaction-info-card-title"]}>
                {t(PAGE_LOCALE_KEY + "infoCard.transMonth.title")}
              </TextMAS>
              <TextMAS className={s["transaction-info-card-amount"]}>
                {`HKD ${
                  amountConversion(
                    dataSource?.monthlyStats?.[0]?.totalVolume,
                  ) ?? "-"
                } (${
                  amountConversion(dataSource?.monthlyStats?.[0]?.totalCount) ??
                  "-"
                })`}
              </TextMAS>
            </Stack>
          </Card>
          <Card>
            <Stack direction="column">
              <TextMAS className={s["transaction-info-card-title"]}>
                {t(PAGE_LOCALE_KEY + "infoCard.transYear.title")}
              </TextMAS>
              <TextMAS className={s["transaction-info-card-amount"]}>
                {`HKD ${
                  amountConversion(dataSource?.yearlyStats?.[0]?.totalVolume) ??
                  "-"
                } (${
                  amountConversion(dataSource?.yearlyStats?.[0]?.totalCount) ??
                  "-"
                })`}
              </TextMAS>
            </Stack>
          </Card>
        </Stack>

        <div className={s["transaction-year-change-container"]}>
          <TextMAS fontSize="large" fontWeight="semibold">
            {t(PAGE_LOCALE_KEY + "transactionYearChange.title")}
          </TextMAS>
          <AnalyticsTable
            columns={transactionYearChangeColumns}
            data={dataSource?.yearlyStats}
            tableStyle={{ width: "1050px" }}
          />
        </div>
        <div className={s["transaction-month-change-container"]}>
          <TextMAS fontSize="large" fontWeight="semibold">
            {t(PAGE_LOCALE_KEY + "transactionMonthChange.title")}
          </TextMAS>
          <AnalyticsTable
            columns={transactionMonthChangeColumns}
            data={dataSource?.monthlyStats}
            tableStyle={{ width: "1050px" }}
          />
        </div>
        <div className={s["transaction-month-change-container"]}>
          <TextMAS fontSize="large" fontWeight="semibold">
            {t(PAGE_LOCALE_KEY + "transactionCardType.title")}
          </TextMAS>
          <AnalyticsTable
            initSort={{
              key: "volume",
              asc: false,
            }}
            columns={transactionCardTypeColumns}
            data={dataSource?.byCardType}
            tableStyle={{ width: "1050px" }}
          />
        </div>
      </div>
    </PageContainer>
  );
};

export default React.memo(StatisticsOnlineTransaction);
